import { ApplicationConfig, provideBrowserGlobalErrorListeners, isDevMode, APP_INITIALIZER } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';

registerLocaleData(localePt);
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { DatePipe, DecimalPipe } from '@angular/common';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { PrimeNGConfig } from './core/config/primeng-theme.config';

import { routes } from './app.routes';
import { provideStore } from '@ngrx/store';
import { authReducer } from './core/store/auth/auth.reducer';
import { provideEffects } from '@ngrx/effects';
import { AuthEffects } from './core/store/auth/auth.effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';

import { DialogService } from 'primeng/dynamicdialog';
import { RoutingAwareDialogService } from './core/services/routing-aware-dialog.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { SupabaseService } from './core/services/supabase.service';
import { Store } from '@ngrx/store';
import * as AuthActions from './core/store/auth/auth.actions';

// App initializer to load session before routing
function initializeApp(supabase: SupabaseService, store: Store) {
  return () => {
    return new Promise(async (resolve) => {
      try {
        const { data } = await supabase.client.auth.getSession();
        if (data.session) {
          store.dispatch(AuthActions.sessionLoaded({ session: data.session }));

          // Wait for permissions to be loaded by the effect
          const permissionsLoaded = new Promise((permResolve) => {
            const subscription = store.select(state => (state as any).auth?.permissions).subscribe(permissions => {
              if (permissions !== null && permissions !== undefined) {
                subscription.unsubscribe();
                permResolve(true);
              }
            });

            // Timeout after 10 seconds to prevent infinite wait
            setTimeout(() => {
              subscription.unsubscribe();
              permResolve(true);
            }, 10000);
          });

          await permissionsLoaded;
        }
      } catch (e) {
        console.error('Session initialization error:', e);
      }
      resolve(true);
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withFetch()),
    provideStore({ auth: authReducer }),
    provideEffects([AuthEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideTranslateService({
      defaultLanguage: 'en'
    }),
    provideTranslateHttpLoader({
      prefix: 'assets/i18n/',
      suffix: '.json'
    }),
    provideAnimationsAsync(),
    providePrimeNG(PrimeNGConfig),
    // Closes any open dynamic dialog when the router navigates - see
    // RoutingAwareDialogService. Browser Back used to change the page while
    // leaving the modal floating over the new one.
    { provide: DialogService, useClass: RoutingAwareDialogService },
    MessageService,
    ConfirmationService,
    DatePipe,
    DecimalPipe,
    provideLottieOptions({
      player: () => player,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [SupabaseService, Store],
      multi: true
    },
  ]
};
